import { GetServerSideProps } from 'next';
import QueryString from 'qs';

import CampaignLayout from '@/components/campaign';
import DiscountLandingWrapper from '@/components/discountLanding/DiscountLandingWrapper';
import Footer from '@/components/footer/FooterWrapper';
import Header from '@/components/header/Header';
import Layout from '@/components/layout/LayoutWrapper';
import { InitRequest, useUserState } from '@/hooks/store';
import { getCampaign } from '@/server/initState/getCampaign';
import { Campaign } from '@/store/campaign/types';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

const Campaigns: React.FC<{
	campaign: Campaign;
}> = ({ campaign }) => {
	const user = useUserState();

	const isPodcastCampaign = campaign.utm_medium === 'podcast';
	const isSubscribed = user?.subscription?.valid ?? false;
	const welcomeText = isSubscribed
		? campaign.subscribed_banner_string
		: campaign.not_subscribed_banner_string;

	return (
		<Layout pageName="campaigns" title="Calm - The #1 App for Meditation and Sleep">
			{isPodcastCampaign ? (
				<DiscountLandingWrapper welcomeText={welcomeText} />
			) : (
				<>
					<Header />
					<CampaignLayout campaign={campaign} />
				</>
			)}
			<Footer />
		</Layout>
	);
};

export default Campaigns;

const getCouponQueryParam = (campaign: Campaign) => {
	if (!campaign.coupon) return {};

	return campaign.plan === 'lifetime'
		? { lifetime_coupon: campaign.coupon }
		: { yearly_coupon: campaign.coupon };
};

export const getServerSideProps: GetServerSideProps = async context => {
	const { params, query } = context;

	try {
		if (typeof params?.campaignSlug === 'string') {
			// Get campaign (cached automatically)
			const campaign = await getCampaign(params.campaignSlug);

			if (!campaign) {
				return { notFound: true };
			}

			const couponQueryParam = getCouponQueryParam(campaign);

			const finalQuery = {
				...campaign,
				...query,
				...couponQueryParam,
				vanity_url: `/${campaign.slug}`,
			};

			// Only redirect if we're not already on the final campaign URL with params
			if (params.campaignSlug !== campaign.slug || !query.vanity_url) {
				const queryString = QueryString.stringify(finalQuery);
				return {
					redirect: {
						destination: `/c/${campaign.slug}${queryString ? `?${queryString}` : ''}`,
						permanent: false,
					},
				};
			}

			const initialState = await getInitialServerState(
				context,
				[InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
				{
					coupon: campaign.coupon,
				},
			);

			return {
				props: {
					campaign,
					initialState: {
						...initialState,
						campaign,
					},
				},
			};
		}
	} catch (err) {
		return {
			notFound: true,
		};
	}

	return {
		notFound: true,
	};
};
